// =============================================================================
// io/sync/backtests.ts — load, save and delete manual back-test samples.
// -----------------------------------------------------------------------------
// The Backtesting tab's data layer. Transcribed from the old client's
// `_backtestToDb` serializer, its `byTable.backtests` load mapping and the
// `backtests` migration.
//
// ── 🚨 LEAN COLUMNS, EVERYTHING ELSE RIDES `extras` ──
//
// The table stores thirteen typed columns; `rrPlanned`, the two times, session,
// regime, news, the criteria/confluence marks, the rule-version stamp and
// `createdAt` all live in the `extras` JSONB — the same forward-compat convention
// as trades and patterns. So this record carries its ORIGINAL `extras` blob and a
// save MERGES the modelled riders over it (spread first, riders on top), never
// rebuilds it. Rebuilding would drop `sourceTradeId` — the import dedup marker —
// and any key a future version adds, which is D62 exactly.
//
// ── 🚨 WHY NO HYDRATION LEDGER, WHERE A TRADE NEEDS ONE (D46) ──
//
// `loadBacktests` reads every column AND the whole `extras` blob, and the edit
// form renders every field. There is no partial read a save could write back
// over — the safety property is the shape of the read, not a guard. ⚠ Check that
// again if this ever grows a column the form does not show.
//
// ── UPDATE BY ID, INSERT FOR NEW ──
//
// Same split as every other writer here (D-note in saveExitModel): an upsert on a
// mistyped id invents a phantom row; an update matching nothing is detectable and
// a colliding insert is a loud duplicate-key error. Adding a sample INSERTs it
// immediately — matching beta, which pushes the blank record and saves at once —
// so every later inline edit is an UPDATE.
//
// ⚠ `pattern_id` / `exit_model_id` are plain TEXT with NO foreign key, on purpose:
// a deleted pattern must not abort the save or cascade research away. The tab
// renders an orphan group as "(deleted pattern)".
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { reportFailureAs } from '../diagnostics/errorLog';
import { PAGE_ROWS } from './load';

/** One hand-recorded back-test sample, in memory. */
export interface BacktestRecord {
  id: string;
  patternId: string;
  /** 'YYYY-MM-DD' or '' — a DATE column that can be null. */
  date: string;
  /** 'long' | 'short' | '' */
  side: string;
  contract: string;
  entry: number | null;
  exit: number | null;
  riskPoints: number | null;
  /** Canonical planned R:R (reward ÷ risk). Rides `extras`. */
  rrPlanned: number | null;
  maePoints: number | null;
  mfePoints: number | null;
  timeIn: string;
  timeOut: string;
  session: string;
  regime: string;
  newsEvent: string;
  exitModelId: string;
  /** `{ [criterionId]: true }` — pattern-rule-version specific. Rides `extras`. */
  criteriaMet: Record<string, boolean>;
  /** `{ [confluenceId]: { active: true } }` — same shape as trades. Rides `extras`. */
  confluences: Record<string, { active?: boolean }>;
  notes: string;
  screenshots: string[];
  /** Which rule-version this sample tests. `null` when unknown. Rides `extras`. */
  versionTested: number | null;
  /** ISO timestamp, the secondary sort key. Rides `extras`. */
  createdAt: string;
  /**
   * ⚠ THE WHOLE `extras` blob as loaded, kept so a save merges over it rather than
   * rebuilding — preserving `sourceTradeId` and any key this client does not model.
   */
  extras: Record<string, unknown>;
}

const BACKTESTS_SELECT =
  'id,pattern_id,date,side,contract,entry,exit,risk_points,mae_points,mfe_points,'
  + 'exit_model_id,notes,screenshots,extras';

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const strArr = (v: unknown): string[] =>
  (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const numOrNull = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const x = Number(v);
  return Number.isFinite(x) ? x : null;
};
const obj = (v: unknown): Record<string, never> =>
  (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, never>) : {});

export function backtestFromDb(r: Record<string, unknown>): BacktestRecord {
  const extras = obj(r['extras']) as Record<string, unknown>;
  return {
    id: String(r['id'] ?? ''),
    patternId: str(r['pattern_id']),
    date: r['date'] == null ? '' : String(r['date']),
    side: str(r['side']),
    contract: str(r['contract']),
    entry: numOrNull(r['entry']),
    exit: numOrNull(r['exit']),
    riskPoints: numOrNull(r['risk_points']),
    maePoints: numOrNull(r['mae_points']),
    mfePoints: numOrNull(r['mfe_points']),
    exitModelId: str(r['exit_model_id']),
    notes: str(r['notes']),
    screenshots: strArr(r['screenshots']),
    // ── riders ──
    rrPlanned: numOrNull(extras['rrPlanned']),
    timeIn: str(extras['timeIn']),
    timeOut: str(extras['timeOut']),
    session: str(extras['session']),
    regime: str(extras['regime']),
    newsEvent: str(extras['newsEvent']),
    criteriaMet: obj(extras['criteriaMet']) as Record<string, boolean>,
    confluences: obj(extras['confluences']) as Record<string, { active?: boolean }>,
    versionTested: numOrNull(extras['versionTested']),
    createdAt: str(extras['createdAt']),
    extras,
  };
}

/**
 * Build the row from a record. ⚠ `extras` is spread FIRST and the riders written
 * over it, so a key this client does not model survives the round trip.
 */
export function backtestToRow(rec: BacktestRecord, userId: string): Record<string, unknown> {
  return {
    id: rec.id,
    user_id: userId,
    pattern_id: rec.patternId || '',
    date: rec.date || null,
    side: rec.side || '',
    contract: rec.contract || '',
    entry: numOrNull(rec.entry),
    exit: numOrNull(rec.exit),
    risk_points: numOrNull(rec.riskPoints),
    mae_points: numOrNull(rec.maePoints),
    mfe_points: numOrNull(rec.mfePoints),
    exit_model_id: rec.exitModelId || '',
    notes: rec.notes || '',
    screenshots: rec.screenshots || [],
    extras: {
      ...rec.extras,
      rrPlanned: rec.rrPlanned,
      timeIn: rec.timeIn || '',
      timeOut: rec.timeOut || '',
      session: rec.session || '',
      regime: rec.regime || '',
      newsEvent: rec.newsEvent || '',
      criteriaMet: rec.criteriaMet || {},
      confluences: rec.confluences || {},
      versionTested: rec.versionTested,
      createdAt: rec.createdAt || null,
    },
  };
}

/**
 * Every sample, user-scoped. Paged to exhaustion — the manual importer can add
 * hundreds at once, and a single request is only the first `PAGE_ROWS` of them.
 * Ordered by `id` so page boundaries are stable under a concurrent insert; the
 * tab re-sorts by date anyway.
 */
export async function loadBacktests(sb: SupabaseClient): Promise<BacktestRecord[]> {
  const out: BacktestRecord[] = [];
  for (let from = 0; ; from += PAGE_ROWS) {
    const { data, error } = await sb
      .from('backtests')
      .select(BACKTESTS_SELECT)
      .order('id', { ascending: true })
      .range(from, from + PAGE_ROWS - 1);

    if (error) {
      throw new Error(
        `loadBacktests failed on page ${(from / PAGE_ROWS) + 1}: ${error.message}. `
        + 'Refusing a partial list — a short set of samples reads as "some research is gone".',
      );
    }
    if (!data || !data.length) break;
    for (const r of data as unknown as Record<string, unknown>[]) out.push(backtestFromDb(r));
    if (data.length < PAGE_ROWS) break;
  }
  return out;
}

export interface SaveBacktestResult {
  id: string | null;
  error: string | null;
}

/** Update an existing sample by id. */
export async function saveBacktest(
  sb: SupabaseClient, rec: BacktestRecord, userId: string,
): Promise<SaveBacktestResult> {
  const row = backtestToRow(rec, userId);
  // ⚠ `id`/`user_id` are the match keys, not payload — dropping them means an
  // UPDATE never rewrites them to themselves.
  const { id: _id, user_id: _uid, ...patch } = row;
  const { data, error } = await sb
    .from('backtests').update(patch).eq('id', rec.id).eq('user_id', userId).select('id');

  if (error) {
    return { id: null, error: reportFailureAs('save backtest', error, `Save failed: ${error.message}`) };
  }
  // ⚠ The no-rows branch below is NOT logged: nothing failed, the sample is just
  // not there any more, and there is no error to record.
  if (!Array.isArray(data) || data.length === 0) {
    return {
      id: null,
      error: 'The save matched no rows, so nothing changed. The sample may have been '
        + 'deleted, or it belongs to another user.',
    };
  }
  return { id: rec.id, error: null };
}

/** Insert a brand-new sample. */
export async function insertBacktest(
  sb: SupabaseClient, rec: BacktestRecord, userId: string,
): Promise<SaveBacktestResult> {
  if (!rec.id) return { id: null, error: 'Refusing to insert a sample with no id.' };
  const { error } = await sb.from('backtests').insert(backtestToRow(rec, userId));
  if (error) {
    // ⚠ Its own scope. An insert failing and an update failing are different
    // problems (a duplicate id vs a row that moved), and the user-facing wording
    // is identical — the log line is the only place they can be told apart.
    return { id: null, error: reportFailureAs('add backtest', error, `Save failed: ${error.message}`) };
  }
  return { id: rec.id, error: null };
}

export async function deleteBacktest(
  sb: SupabaseClient, userId: string, id: string,
): Promise<{ error: string | null }> {
  const { data, error } = await sb
    .from('backtests').delete().eq('user_id', userId).eq('id', id).select('id');
  if (error) {
    return { error: reportFailureAs('delete backtest', error, `Delete failed: ${error.message}`) };
  }
  if (!Array.isArray(data) || data.length === 0) {
    return { error: 'The delete matched no rows — the sample may already be gone.' };
  }
  return { error: null };
}
